import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { CalendarIcon, X, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { format, addDays } from 'date-fns';
import { useLanguage } from '@/contexts/LanguageContext';
import { cn } from '@/lib/utils';
import type { DateRange } from 'react-day-picker';

interface LogsTimeFilterProps {
  value: { start: Date | null; end: Date | null };
  onChange: (value: { start: Date | null; end: Date | null }) => void;
  title?: string;
}

export function LogsTimeFilter({ value, onChange, title }: LogsTimeFilterProps) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState<DateRange | undefined>({
    from: value.start || undefined,
    to: value.end || undefined,
  });
  const { t, dir } = useLanguage();
  const isRTL = dir === 'rtl';

  const hasFilter = value.start || value.end;
  const displayText = hasFilter 
    ? `${value.start ? format(value.start, 'PP') : '...'} - ${value.end ? format(value.end, 'PP') : '...'}`
    : title || t('filters.timeTitle');

  const clearFilter = () => {
    onChange({ start: null, end: null });
    setDate(undefined);
  };

  const handleDateSelect = (range: DateRange | undefined) => {
    setDate(range);
    if (range?.from) {
      onChange({ 
        start: range.from, 
        end: range.to || range.from 
      });
    } else {
      onChange({ start: null, end: null });
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="outline" 
          className={`h-9 w-full ${isRTL ? 'flex-row-reverse' : ''}`}
        >
          {displayText}
          <ChevronDown className={`h-4 w-4 ${isRTL ? 'mr-2' : 'ml-2'}`} />
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className={`w-auto p-0 ${isRTL ? 'text-right' : 'text-left'}`} 
        align={isRTL ? "start" : "end"}
      >
        <div className={`p-3 space-y-3 ${isRTL ? 'text-right' : 'text-left'}`}>
          <div>
            <Calendar
              initialFocus
              mode="range"
              defaultMonth={date?.from}
              selected={date}
              onSelect={handleDateSelect}
              numberOfMonths={2}
              className={isRTL ? 'rtl' : 'ltr'}
              classNames={{
                months: isRTL ? "flex flex-col sm:flex-row space-y-4 sm:space-y-0 [&>*:first-child]:sm:ml-8" : "flex flex-col sm:flex-row space-y-4 sm:space-x-8 sm:space-y-0",
                nav_button_previous: isRTL ? "absolute right-1" : "absolute left-1",
                nav_button_next: isRTL ? "absolute left-1" : "absolute right-1",
              }}
              components={{
                IconLeft: ({ ...props }) => (
                  isRTL ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />
                ),
                IconRight: ({ ...props }) => (
                  isRTL ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />
                ),
              }}
            />
          </div>
          {hasFilter && (
            <Button
              variant="outline"
              size="sm"
              onClick={clearFilter}
              className={`w-full ${isRTL ? 'flex-row-reverse' : ''}`}
            >
              <X className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
              {t('filters.clear')}
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
